import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import diacritics from 'diacritics';
import { BZ2_FILTER_MODAL_CLOSE, BZ2_NO_SUCH_OPTION, CONFIRM } from 'mk/autogenerated/translations/FacetDropdown.b0df6e7d1a5d2dc5535eaa09aabc3b43'
import FacetDropdownOption, { OptionKey } from 'mk2/apps/bazaar/components/FacetDropdownOption';
import useClickOutside from 'mk2/hooks/useClickOutside';
import useEventCallback from 'mk2/hooks/useEventCallback';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './FacetDropdown.mscss';

function toSearchText(text: string) {
    if (!text) {
        return '';
    }
    return (
        diacritics
            .remove(text)
            // [^\w] everything that is not a digit, word or
            // underscore
            .replace(/[^\w]/g, '')
    );
}

function normalizeSearch(search: string) {
    const qStripped = toSearchText(search);
    const anyChar = '.*';
    return new RegExp(anyChar + qStripped + anyChar, 'i');
}
export interface Option {
    key: OptionKey;
    label: string;
    count?: number;
    limit?: number; // Is used?
    ad?: string; // Is used?
    newFeature?: boolean; // Is used?
    optionClassName?: string; // Is used?
}

export interface OwnProps {
    className?: string;
    multiselect?: boolean;
    search?: boolean;
    facetKey: string;
    options: Option[];
    alwaysDisplayed?: OptionKey[];
    selected?: OptionKey[];

    // This options will be always displayed first, then selected, then rest
    settingsOptions?: OptionKey[];

    onChange(facetKey: string, optionKey: OptionKey, checked: boolean);
    onClose();
}

type Props = OwnProps;

const emptyArray = [];

const FacetDropdown: React.FunctionComponent<Props> = ({
    className,
    facetKey,
    multiselect = false,
    search = false,
    options,
    alwaysDisplayed = emptyArray,
    selected = emptyArray,
    settingsOptions= emptyArray,
    onChange,
    onClose,
}) => {
    const [filter, setFilter] = useState('');
    const [initiallySelected] = useState(selected.filter((i) => !settingsOptions.includes(i)));
    const inputRef = useRef<HTMLInputElement>();
    const containerRef = useRef<HTMLDivElement>();

    const onFilterChange = useEventCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            setFilter(event.currentTarget.value);
        },
        [setFilter],
    );

    const onContainerClick = useEventCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    }, []);

    const settingsOptionsOptions = useMemo(() => options.filter((opt) => settingsOptions.includes(opt.key)), [
        options,
        settingsOptions,
    ]);
    const selectedOptions = useMemo(() => options.filter((opt) => initiallySelected.includes(opt.key)), [
        options,
        initiallySelected,
    ]);
    const searchText = normalizeSearch(filter);
    const filteredOptions = useMemo(
        () =>
            options.filter(
                (opt) =>
                    !initiallySelected.includes(opt.key) &&
                    !settingsOptions.includes(opt.key) &&
                    (searchText.test(toSearchText(opt.label)) ||
                        (alwaysDisplayed && alwaysDisplayed.includes(opt.key))),
            ),
        [filter, options, initiallySelected, alwaysDisplayed, settingsOptions],
    );

    // Focus search on mount
    useEffect(() => {
        if (inputRef.current) {
            // search can be disabled
            inputRef.current.focus();
        }
    }, []);

    // Click outside
    useClickOutside(containerRef, onClose);

    return (
        <div className={cx(styles.FacetDropdown, className)} onClick={onContainerClick} ref={containerRef}>
            <div className={styles.FacetDropdown__content}>
                <div className={styles.FacetDropdown__header}>
                    {search && (
                        <label className={styles.FacetDropdown__header__search}>
                            <FontAwesomeIcon
                                icon={faSearch}
                                flip="horizontal"
                                className={styles.FacetDropdown__header__search__icon}
                            />
                            <input
                                className={styles.FacetDropdown__header__search__input}
                                type="search"
                                value={filter}
                                onChange={onFilterChange}
                                ref={inputRef}
                            />
                        </label>
                    )}
                    <div className={styles.FacetDropdown__header__dismiss}>
                        <button
                            className={styles.FacetDropdown__header__dismiss__button}
                            type="button"
                            onClick={onClose}
                        >
                            {!selected.length ? BZ2_FILTER_MODAL_CLOSE : CONFIRM}
                        </button>
                    </div>
                </div>
                <div className={styles.FacetDropdown__options}>
                    {settingsOptionsOptions.length > 0 && (
                        <div className={styles.FacetDropdown__options__settings}>
                            {settingsOptionsOptions.map((option) => (
                                <FacetDropdownOption
                                    key={option.key}
                                    type={multiselect ? 'checkbox' : 'radio'}
                                    facetKey={facetKey}
                                    optionKey={option.key}
                                    label={option.label}
                                    selected={selected.includes(option.key)}
                                    count={option.count}
                                    limit={option.limit}
                                    ad={option.ad}
                                    newFeature={option.newFeature}
                                    optionClassName={option.optionClassName}
                                    onChange={onChange}
                                />
                            ))}
                        </div>
                    )}
                    {selectedOptions.length > 0 && (
                        <div className={styles.FacetDropdown__options__selected}>
                            {selectedOptions.map((option) => (
                                <FacetDropdownOption
                                    key={option.key}
                                    type={multiselect ? 'checkbox' : 'radio'}
                                    facetKey={facetKey}
                                    optionKey={option.key}
                                    label={option.label}
                                    selected={selected.includes(option.key)}
                                    count={option.count}
                                    limit={option.limit}
                                    ad={option.ad}
                                    newFeature={option.newFeature}
                                    optionClassName={option.optionClassName}
                                    onChange={onChange}
                                />
                            ))}
                        </div>
                    )}
                    {filteredOptions.map((option) => (
                        <FacetDropdownOption
                            key={option.key}
                            type={multiselect ? 'checkbox' : 'radio'}
                            facetKey={facetKey}
                            optionKey={option.key}
                            label={option.label}
                            count={option.count}
                            limit={option.limit}
                            ad={option.ad}
                            newFeature={option.newFeature}
                            optionClassName={option.optionClassName}
                            selected={selected.includes(option.key)}
                            onChange={onChange}
                            className={cx(
                                styles.FacetDropdown__option,
                                styles[`FacetDropdown__option--${facetKey}-${option.key}`],
                            )}
                        />
                    ))}
                    {!filteredOptions.length && filter && (
                        <div className={styles.FacetDropdown__options__empty}>{BZ2_NO_SUCH_OPTION}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

FacetDropdown.displayName = 'FacetDropdown';

export default React.memo<Props>(FacetDropdown);
